import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/features/auth/session';
import { useInviteOrganizationAdmin } from '@/features/membership/hooks';
import { InvitationSuccessDialog } from '@/features/membership/invitation-success-dialog';
import type { InviteToOrganizationResult } from '@/features/membership/types';
import { useCreateOrganization } from '@/features/platform/hooks';
import type { OrganizationType, OrganizationView } from '@/features/organization/types';
import { ApiError } from '@/lib/api/client';

const ORGANIZATION_TYPE_LABELS: Readonly<Record<OrganizationType, string>> = {
  PESANTREN: 'Pesantren',
  SCHOOL: 'Sekolah',
  RUMAH_TAHFIDZ: 'Rumah Tahfidz',
  MOSQUE: 'Masjid',
  PERSONAL: 'Perorangan',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function apiErrorMessage(error: ApiError | Error | null): string | null {
  if (error === null) {
    return null;
  }
  return error instanceof ApiError ? error.message : 'Terjadi kesalahan. Silakan coba lagi.';
}

/**
 * The minimum Platform Super Admin surface (G-9, Job 6): create the first
 * Pondok organization, then invite its first Organization Admin - the two
 * already-ratified, already-implemented, already-tested backend
 * capabilities (ADR-020, ADR-027) that previously had no UI at all,
 * requiring raw API calls. Deliberately narrow: no organization edit, no
 * deletion, no membership list, no role change, no analytics, no billing,
 * no generic platform dashboard (Job 6's own UX boundary).
 *
 * Reached via `RequirePlatformCapability` (app/require-platform-capability.tsx),
 * a route guard sitting outside `RequireActiveOrganization` - this page
 * renders correctly for a Platform Super Admin with zero organization
 * memberships, which is the ordinary case for that role.
 */
export function PlatformAdminPage(): JSX.Element {
  const { user, logout } = useAuth();

  const [name, setName] = useState('');
  const [organizationType, setOrganizationType] = useState<OrganizationType>('PESANTREN');
  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const createOrganization = useCreateOrganization();

  const [createdOrganization, setCreatedOrganization] = useState<OrganizationView | null>(null);

  const [adminEmail, setAdminEmail] = useState('');
  const [adminValidationError, setAdminValidationError] = useState<string | null>(null);
  const inviteOrganizationAdmin = useInviteOrganizationAdmin();
  const [invitationResult, setInvitationResult] = useState<InviteToOrganizationResult | null>(null);

  function handleCreateSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    if (name.trim().length === 0) {
      setValidationError('Nama organisasi wajib diisi.');
      return;
    }
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug.trim().toLowerCase()) || slug.trim().length < 3) {
      setValidationError('Slug harus huruf kecil, angka, dan tanda hubung, minimal 3 karakter.');
      return;
    }
    if (email.trim().length > 0 && !EMAIL_PATTERN.test(email.trim())) {
      setValidationError('Masukkan alamat email yang valid.');
      return;
    }

    createOrganization.mutate(
      {
        name: name.trim(),
        organizationType,
        slug: slug.trim().toLowerCase(),
        email: email.trim().length > 0 ? email.trim() : null,
        phone: phone.trim().length > 0 ? phone.trim() : null,
      },
      { onSuccess: (organization) => setCreatedOrganization(organization) },
    );
  }

  function handleInviteAdminSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setAdminValidationError(null);

    if (createdOrganization === null) {
      return;
    }
    if (!EMAIL_PATTERN.test(adminEmail.trim())) {
      setAdminValidationError('Masukkan alamat email yang valid.');
      return;
    }

    inviteOrganizationAdmin.mutate(
      { organizationId: createdOrganization.id, email: adminEmail.trim() },
      {
        onSuccess: (result) => {
          setInvitationResult(result);
          setAdminEmail('');
        },
      },
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex items-center justify-between border-b border-border p-4">
        <div>
          <span className="text-lg font-semibold text-primary">Mahfuz</span>
          <span className="ml-2 text-sm text-muted-foreground">Platform Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-muted-foreground sm:inline">{user?.email}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void logout();
            }}
          >
            Keluar
          </Button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col gap-6 p-4">
        <div>
          <h1 className="text-xl font-semibold">Buat Organisasi &amp; Undang Admin Pertama</h1>
          <p className="text-sm text-muted-foreground">
            Buat organisasi Pondok baru, lalu undang Organization Admin pertamanya.
          </p>
        </div>

        {createdOrganization === null ? (
          <form
            onSubmit={handleCreateSubmit}
            className="flex flex-col gap-4"
            aria-label="Buat organisasi"
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="org-name">Nama Organisasi</Label>
              <Input
                id="org-name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
                placeholder="Pondok Pesantren Contoh"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="org-type">Jenis Organisasi</Label>
              <Select
                value={organizationType}
                onValueChange={(value) => {
                  setOrganizationType(value as OrganizationType);
                }}
              >
                <SelectTrigger id="org-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ORGANIZATION_TYPE_LABELS) as OrganizationType[]).map((type) => (
                    <SelectItem key={type} value={type}>
                      {ORGANIZATION_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="org-slug">Slug</Label>
              <Input
                id="org-slug"
                value={slug}
                onChange={(event) => {
                  setSlug(event.target.value);
                }}
                placeholder="pondok-contoh"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="org-email">Email (opsional)</Label>
              <Input
                id="org-email"
                type="text"
                inputMode="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                placeholder="admin@pondokcontoh.org"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="org-phone">Telepon (opsional)</Label>
              <Input
                id="org-phone"
                type="text"
                inputMode="tel"
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                }}
                placeholder="08xxxxxxxxxx"
              />
            </div>

            {validationError !== null && (
              <Alert variant="destructive">
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}
            {apiErrorMessage(createOrganization.error) !== null && (
              <Alert variant="destructive">
                <AlertDescription>{apiErrorMessage(createOrganization.error)}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" disabled={createOrganization.isPending}>
              {createOrganization.isPending ? 'Membuat...' : 'Buat Organisasi'}
            </Button>
          </form>
        ) : (
          <div className="flex flex-col gap-6">
            <Alert>
              <AlertDescription>
                <span className="font-medium">{createdOrganization.name}</span> berhasil dibuat
                (slug: {createdOrganization.slug}, jenis:{' '}
                {ORGANIZATION_TYPE_LABELS[createdOrganization.organizationType as OrganizationType]}
                ).
              </AlertDescription>
            </Alert>

            <form
              onSubmit={handleInviteAdminSubmit}
              className="flex flex-col gap-4"
              aria-label="Undang Organization Admin"
            >
              <div className="flex flex-col gap-2">
                <Label htmlFor="admin-email">Email Organization Admin</Label>
                <Input
                  id="admin-email"
                  type="text"
                  inputMode="email"
                  value={adminEmail}
                  onChange={(event) => {
                    setAdminEmail(event.target.value);
                  }}
                  placeholder="admin@pondokcontoh.org"
                />
              </div>

              {adminValidationError !== null && (
                <Alert variant="destructive">
                  <AlertDescription>{adminValidationError}</AlertDescription>
                </Alert>
              )}
              {apiErrorMessage(inviteOrganizationAdmin.error) !== null && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {apiErrorMessage(inviteOrganizationAdmin.error)}
                  </AlertDescription>
                </Alert>
              )}

              <Button type="submit" disabled={inviteOrganizationAdmin.isPending}>
                {inviteOrganizationAdmin.isPending ? 'Mengirim...' : 'Undang Organization Admin'}
              </Button>
            </form>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setCreatedOrganization(null);
                setName('');
                setSlug('');
                setEmail('');
                setPhone('');
              }}
            >
              Buat Organisasi Lain
            </Button>
          </div>
        )}
      </main>

      <InvitationSuccessDialog
        result={invitationResult}
        onClose={() => {
          setInvitationResult(null);
        }}
      />
    </div>
  );
}
